import React, { useState } from 'react';
import { Link } from 'gatsby';
import usePortal from 'react-useportal';
import GenericLink from './GenericLink';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';

import Img from 'gatsby-image/withIEPolyfill';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { convertRToSuperscript } from '../utils/utils';
import { GatsbySanityImageFluid, GatsbySanityImageFluidNoBase64 } from '../types';
import Layout from './Layout';
import ShareCard from '../components/ShareCard';

import * as styles from './DetailsPageLayout.module.scss';
import { HIDDEN_FEATURES } from '../constants';

import blexia from '../assets/images/blexia_horizontal.svg';
import { FaTag } from 'react-icons/fa';

interface DetailsPageLayoutProps {
  turnBackLink?: {
    to: string;
    title: string;
  };
  withShareLink?: boolean;
  imageFluid?: GatsbySanityImageFluid | GatsbySanityImageFluidNoBase64;
  imageAlt?: string;
  title: string;
  date?: string;
  categories?: Array<{
    title: string;
  }>;
  barsImage?: string;
  fullWithPage?: boolean;
  borderColor?: string;
  blexiaProduct?: boolean;
  imageTitle?: GatsbySanityImageFluid | GatsbySanityImageFluidNoBase64;
  children: React.ReactNode;
}

const DetailsPageLayout = ({
  turnBackLink,
  withShareLink,
  imageFluid,
  imageAlt,
  title,
  date,
  categories,
  barsImage,
  fullWithPage = false,
  borderColor,
  blexiaProduct,
  imageTitle,
  children,
}: DetailsPageLayoutProps): React.ReactElement => {
  const { t } = useTranslation();
  const { Portal } = usePortal();
  const [isShareCardVisible, setIsShareCardVisible] = useState(false);

  function openShareCard() {
    if (navigator.share) {
      navigator
        .share({
          title,
          url: window.location.href,
        })
        .catch(error => {
          Sentry.captureException(error);
          console.error(error);
        });
    } else {
      setIsShareCardVisible(true);
    }
  }

  const shareButton = (
    <GenericLink styleOnly className={styles.link} onClick={openShareCard}>
      {t('share_article.link', 'Share')}
      <FontAwesomeIcon icon="share" className={styles.iconRight} />
    </GenericLink>
  );
  return (
    <Layout className="details-page">
      <div className={styles.section}>
        <div className={styles.container}>
          <div className={styles.topLinksContainer}>
            {!!turnBackLink ? (
              <Link to={turnBackLink.to} className={styles.link}>
                <FontAwesomeIcon icon="chevron-left" className={styles.iconLeft} />
                {turnBackLink.title}
              </Link>
            ) : (
              // Add invisible span just so the share button appears to the right (because of space-between)
              <span></span>
            )}
            {!!withShareLink && !HIDDEN_FEATURES.includes('share_buttons') && shareButton}
          </div>
          {!!imageFluid && (
            <Img
              className={styles.image}
              loading="eager"
              fadeIn={false}
              fluid={imageFluid}
              alt={imageAlt}
            />
          )}

          {blexiaProduct ? (
            <div className={styles.blexiaProductTitle}>
              {!!imageTitle ? (
                <div className={styles.imageTitleContainer}>
                  <Img
                    className={styles.imageTitle}
                    // loading="eager"
                    fadeIn={false}
                    fluid={imageTitle}
                    alt={title}
                    objectFit="contain"
                    objectPosition="0 0"
                  />
                </div>
              ) : (
                <h1 className={styles.title}>{convertRToSuperscript(title)}</h1>
              )}
              <div className={styles.blexiaLogo}>
                <img src={blexia} alt="" />
              </div>
            </div>
          ) : null}

          {barsImage ? (
            <div className={styles.titleAndBarsContainer} style={{ borderColor: borderColor }}>
              <div className={styles.barsContainer}>
                <img src={barsImage} alt=""></img>
              </div>

              {!!imageTitle ? (
                <div className={styles.imageTitleContainer}>
                  <Img
                    className={styles.imageTitle}
                    // loading="eager"
                    fadeIn={false}
                    fluid={imageTitle}
                    alt={title}
                    objectFit="contain"
                    objectPosition="0 0"
                  />
                </div>
              ) : (
                <h1 className={styles.title}>{convertRToSuperscript(title)}</h1>
              )}
            </div>
          ) : (
            <div className={styles.article}>
              <h1 className={styles.title}>{convertRToSuperscript(title)}</h1>
              {(categories?.length || date) && (
                <div className={styles.detailsContainer}>
                  {categories && categories.length > 0 && (
                    <div className={styles.tagContainer}>
                      <FaTag className={styles.categoryIcon}></FaTag>
                      {categories.map((category, i) => (
                        <span key={i} className={styles.categoryTitle + ' titleParagraph'}>
                          {category.title}
                          {i < categories.length - 1 ? ',' : ''}
                        </span>
                      ))}
                    </div>
                  )}
                  {date && <span className={styles.date}>{date}</span>}
                </div>
              )}
              <hr style={{ borderColor: borderColor }} className={styles.titleDivider}></hr>
            </div>
          )}
          <div className={styles.content}>
            <div className={fullWithPage ? styles.productPageBody : styles.articlesBody}>
              {children}
            </div>
          </div>
          {!!withShareLink && !HIDDEN_FEATURES.includes('share_buttons') && (
            <div className={styles.shareButtonBottomContainer}>{shareButton}</div>
          )}
        </div>
      </div>
      {isShareCardVisible && (
        <Portal>
          <div className={styles.overlay} onClick={() => setIsShareCardVisible(false)}>
            <div className={styles.shareCardWrapper} onClick={e => e.stopPropagation()}>
              <ShareCard></ShareCard>
            </div>
          </div>
        </Portal>
      )}
    </Layout>
  );
};

export default DetailsPageLayout;
